import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Tbody, Thead, Tr, Th, Td, Text,
  Button, HStack, Select, Flex, Box, Spacer,
  Input, InputGroup, InputLeftElement, InputRightElement, IconButton
} from '@chakra-ui/react';
import { MdSearch, MdClear } from "react-icons/md";
import moment from 'moment';

interface DocumentSelectionProps {
  documents: any[];
  openDocument: (template_name: string, document_id: string, project_id: string) => void;
}

type SortKey =
  | 'default'
  | 'name'
  | 'type'
  | 'projectId'
  | 'creationDate'
  | 'lastModificationDate';

type SortDir = 'asc' | 'desc' | null;

const PER_PAGE_OPTIONS = [10, 25, 50];
const COLUMNS_COUNT = 5;

const iconFor = (active: boolean, dir: SortDir) => {
  if (!active || dir === null) return '⇵';
  return dir === 'asc' ? '▲' : '▼';
};

const norm = (v: any) => (v == null ? '' : String(v).toLowerCase());

const ROW_BG_EVEN = 'row-even';
const ROW_BG_ODD = 'row-odd';

const truncate40 = (s: string) => (s && s.length > 40 ? s.slice(0, 40) + '…' : s);

const DocumentSelection: React.FC<DocumentSelectionProps> = ({ documents, openDocument }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(PER_PAGE_OPTIONS[0]);
  const [query, setQuery] = useState('');

  const [sortKey, setSortKey] = useState<SortKey>('default');
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const cycleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    setSortKey('default');
    setSortDir(null);
  };

  const filteredDocs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((doc) => {
      const name = norm(doc?.name);
      const type = norm(doc?.template?.description);
      const project = norm(doc?.projectId);
      return name.includes(q) || type.includes(q) || project.includes(q);
    });
  }, [documents, query]);

  useEffect(() => {
    if (query.trim() === '') {
      setSortKey('default');
      setSortDir(null);
    }
  }, [query]);

  const getValue = (doc: any, key: SortKey) => {
    switch (key) {
      case 'name': return doc?.name ?? '';
      case 'type': return doc?.template?.description ?? '';
      case 'projectId': return doc?.projectId ?? '';
      case 'creationDate': return doc?.creationDate ?? null;
      case 'lastModificationDate': return doc?.lastModificationDate ?? null;
      default: return null;
    }
  };

  const compareValues = (a: any, b: any, dir: Exclude<SortDir, null>) => {
    const factor = dir === 'asc' ? 1 : -1;
    const isDateLike = (v: any) =>
      v instanceof Date || (typeof v === 'string' && !isNaN(new Date(v).getTime()));

    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    if (isDateLike(a) && isDateLike(b)) {
      const tA = new Date(a).getTime();
      const tB = new Date(b).getTime();
      if (tA === tB) return 0;
      return tA < tB ? -1 * factor : 1 * factor;
    }

    const nA = typeof a === 'number' ? a : (typeof a === 'string' && !isNaN(+a) ? +a : null);
    const nB = typeof b === 'number' ? b : (typeof b === 'string' && !isNaN(+b) ? +b : null);
    if (nA != null && nB != null) {
      if (nA === nB) return 0;
      return nA < nB ? -1 * factor : 1 * factor;
    }

    const sA = String(a).toLowerCase();
    const sB = String(b).toLowerCase();
    if (sA === sB) return 0;
    return sA < sB ? -1 * factor : 1 * factor;
  };

  const sortedDocs = useMemo(() => {
    const base = [...filteredDocs];
    if (sortKey === 'default') {
      return base.sort(
        (a, b) =>
          new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()
      );
    }
    const dir = sortDir ?? 'asc';
    return base.sort((aDoc, bDoc) => {
      const aVal = getValue(aDoc, sortKey);
      const bVal = getValue(bDoc, sortKey);
      return compareValues(aVal, bVal, dir);
    });
  }, [filteredDocs, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedDocs.length / perPage));
  const startIndex = (currentPage - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, sortedDocs.length);
  const currentDocs = sortedDocs.slice(startIndex, endIndex);

  const fillerRowsCount = Math.max(0, perPage - currentDocs.length);

  useEffect(() => { setCurrentPage(1); }, [documents, perPage, sortKey, sortDir, query]);
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [totalPages, currentPage]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };


  const thClickable = (label: string, key: SortKey, ariaLabel?: string, width?: string) => {
    const active = sortKey === key;
    const icon = iconFor(active, sortDir);
    const ariaSort =
      sortKey === 'default'
        ? 'none'
        : active
          ? (sortDir === 'asc' ? 'ascending' : 'descending')
          : 'none';

    return (
      <Th
        role="button"
        aria-sort={ariaSort as any}
        onClick={() => cycleSort(key)}
        cursor="pointer"
        userSelect="none"
        title={`Sort by ${ariaLabel ?? label}`}
        w={width}
      >
        <HStack spacing={2}>
          <Text>{label}</Text>
          <Text as="span" fontSize="xs" color="gray.500">{icon}</Text>
        </HStack>
      </Th>
    );
  };

  const clearSearch = () => setQuery('');

  const rowBgAt = (pageRowIndex: number) =>
    pageRowIndex % 2 === 0 ? ROW_BG_EVEN : ROW_BG_ODD;

  const onOpenRow = (doc: any) => openDocument(doc.templateName, doc._id, doc.projectId);

  return (
    <div className="saved-documents">
      <Flex align="center" mb={2} gap={3} wrap="wrap">
        <Text className="title" fontSize="2xl">Created documents</Text>

        <InputGroup maxW="360px">
          <InputLeftElement pointerEvents="none">
            <MdSearch />
          </InputLeftElement>
          <Input
            placeholder="Search by name, type or project #"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="sm"
          />
          {query && (
            <InputRightElement>
              <IconButton
                aria-label="Clear search"
                size="xs"
                variant="ghost"
                icon={<MdClear />}
                onClick={clearSearch}
              />
            </InputRightElement>
          )}
        </InputGroup>

        <Spacer />

        <HStack spacing={2}>
          <Text fontSize="sm" color="gray.600">Documents per page</Text>
          <Select
            size="sm"
            width="80px"
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
          >
            {PER_PAGE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Select>
        </HStack>
      </Flex>

      {sortedDocs.length === 0 && (
        <Box mt={1} mb={2}>
          <Text color="gray.500">
            {query ? `No documents found for “${query}”.` : 'No documents found.'}
          </Text>
        </Box>
      )}

      <Table
  variant="simple"
  size="sm"
  sx={{
    tableLayout: 'fixed',
    'th, td': {
      py: 0,
      height: '44px',
      lineHeight: '44px',
      borderColor: 'var(--lighter-gray)',
    },
  }}
>
        <Thead>
          <Tr>
            {thClickable('Name', 'name', undefined, '34%')}
            {thClickable('Type', 'type', undefined, '20%')}
            {thClickable('Project #', 'projectId', 'project number', '10%')}
            {thClickable('Created', 'creationDate', undefined, '18%')}
            {thClickable('Updated', 'lastModificationDate', undefined, '18%')}
          </Tr>
        </Thead>
        <Tbody>
         
          {currentDocs.map((doc, idx) => {
            const pageRowIndex = idx;
            const bg = rowBgAt(pageRowIndex);
            const nameFull = doc.name ?? '';
            const nameShort = truncate40(nameFull);

            return (
              <Tr
                key={doc._id}
                className={`doc-list-row el-hover-table-row ${bg}`}
                cursor="pointer"
                onClick={() => onOpenRow(doc)}
                onKeyDown={(e) => { if (e.key === 'Enter') onOpenRow(doc); }}
                tabIndex={0}
                role="button"
              >
                <Td
                  className="document-list-info"
                  title={nameFull}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <b>{nameShort}</b>
                </Td>

                <Td className="document-list-info">
                  {doc?.template?.description}
                </Td>

                <Td className="document-list-info">
                  {doc.projectId}
                </Td>

                <Td className="document-list-info">
                  <small>
                    {doc.creationDate
                      ? moment(doc.creationDate).format('MMMM Do YYYY, h:mm A')
                      : '—'}
                  </small>
                </Td>

                <Td className="document-list-info">
                  <small>
                    {doc.lastModificationDate
                      ? moment(doc.lastModificationDate).format('MMMM Do YYYY, h:mm A')
                      : 'Just created'}
                  </small>
                </Td>
              </Tr>
            );
          })}

          {Array.from({ length: fillerRowsCount }).map((_, i) => {
            const pageRowIndex = currentDocs.length + i;
            const bg = rowBgAt(pageRowIndex);
            return (
              <Tr key={`placeholder-${i}`} bg={bg}  className={`doc-list-row el-hover-table-row ${bg} placeholder-row`}>
                <Td colSpan={COLUMNS_COUNT}>&nbsp;</Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      <Text mt={2} fontSize="sm" color="gray.600" textAlign="center">
        {`${sortedDocs.length === 0 ? 0 : startIndex + 1}-${endIndex} of ${sortedDocs.length}`}
      </Text>

      {Math.ceil(sortedDocs.length / perPage) > 1 && (
        <HStack justify="center" spacing={2} mt={3}>
          <Button
            size="sm"
            className="el-btn"
            onClick={() => goToPage(currentPage - 1)}
            isDisabled={currentPage === 1}
          >
            Previous
          </Button>

          {(() => {
            const pages: (number | '...')[] = ((() => {
              const arr: (number | '...')[] = [];
              const maxButtons = 7;
              const totalPages = Math.max(1, Math.ceil(sortedDocs.length / perPage));
              if (totalPages <= maxButtons) {
                for (let i = 1; i <= totalPages; i++) arr.push(i);
                return arr;
              }
              const showLeft = Math.max(2, currentPage - 1);
              const showRight = Math.min(totalPages - 1, currentPage + 1);
              arr.push(1);
              if (showLeft > 2) arr.push('...');
              for (let p = showLeft; p <= showRight; p++) arr.push(p);
              if (showRight < totalPages - 1) arr.push('...');
              arr.push(totalPages);
              return arr;
            })());
            return pages.map((p, idx) =>
              p === '...' ? (
                <Box key={`ellipsis-${idx}`} px={2} color="gray.500">…</Box>
              ) : (
                <Button
                  key={p}
                  size="sm"
                  className={currentPage === p ? 'el-btn el-btn-pag' : 'el-btn el-btn-pag el-btn-secondary'}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </Button>
              )
            );
          })()}

          <Button
            size="sm"
            className="el-btn"
            onClick={() => goToPage(currentPage + 1)}
            isDisabled={currentPage === Math.max(1, Math.ceil(sortedDocs.length / perPage))}
          >
            Next
          </Button>
        </HStack>
      )}
    </div>
  );
};

export default DocumentSelection;
